import React, { useState, useRef, useCallback } from 'react';
import '../styles/home.css';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import ProgressBar from '@cloudscape-design/components/progress-bar';
import { addUsage } from './add-usage.js';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const ConvertToGrayscale = ({ isNew }) => {
  const [visible, setVisible] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitValue, setSubmitValue] = useState('Convert');
  const [errorMessage, setErrorMessage] = useState('');
  const [progress, setProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);
  const cancelRef = useRef(false);

  const isPdf = (f) => f?.type === 'application/pdf';
  const isImage = (f) => f?.type?.startsWith('image/');

  const toggleModal = () => {
    setVisible(!visible);
    if (!visible) resetState();
  };

  const resetState = () => {
    setFile(null);
    setPreview(null);
    setErrorMessage('');
    setProgress(0);
    setIsProcessing(false);
    setResult(null);
    setSubmitValue('Convert');
    cancelRef.current = false;
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    if (!isPdf(selectedFile) && !isImage(selectedFile)) {
      setErrorMessage('Please upload a PDF or image file (JPG, PNG)');
      return;
    }

    setFile(selectedFile);
    setErrorMessage('');
    setResult(null);

    if (isImage(selectedFile)) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(selectedFile);
    } else {
      setPreview(null);
    }
  };

  const applyGrayscale = (canvas) => {
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      data[i] = data[i + 1] = data[i + 2] = gray;
    }
    ctx.putImageData(imageData, 0, 0);
  };

  const convertImage = async () => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        applyGrayscale(canvas);

        canvas.toBlob((blob) => {
          const ext = file.name.split('.').pop();
          resolve({
            blob,
            url: URL.createObjectURL(blob),
            filename: file.name.replace(`.${ext}`, `_grayscale.${ext}`),
          });
        }, file.type, 0.92);
      };
      img.src = preview;
    });
  };

  const convertPdf = async () => {
    const { PDFDocument } = await import('pdf-lib');
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await pdfjsLib.getDocument(arrayBuffer).promise;
    const totalPages = pdfDoc.numPages;
    const newPdf = await PDFDocument.create();

    for (let i = 1; i <= totalPages; i++) {
      if (cancelRef.current) throw new Error('Cancelled');

      const page = await pdfDoc.getPage(i);
      const viewport = page.getViewport({ scale: 3 });
      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      await page.render({ canvasContext: ctx, viewport }).promise;
      applyGrayscale(canvas);

      const imgData = canvas.toDataURL('image/png');
      const img = await newPdf.embedPng(await fetch(imgData).then((r) => r.arrayBuffer()));
      const originalViewport = page.getViewport({ scale: 1 });
      const newPage = newPdf.addPage([originalViewport.width, originalViewport.height]);
      newPage.drawImage(img, { x: 0, y: 0, width: originalViewport.width, height: originalViewport.height });

      setProgress(Math.round((i / totalPages) * 100));
    }

    const pdfBytes = await newPdf.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    return {
      blob,
      url: URL.createObjectURL(blob),
      filename: file.name.replace('.pdf', '_grayscale.pdf'),
    };
  };

  const handleConvert = async () => {
    if (!file) {
      setErrorMessage('Please select a file');
      return;
    }

    setSubmitValue('Processing...');
    setIsProcessing(true);
    setProgress(0);
    cancelRef.current = false;

    try {
      const output = isPdf(file) ? await convertPdf() : await convertImage();
      setResult(output);
      setProgress(100);
      setSubmitValue('Convert');
      addUsage('AWESOME PDF TOOLS', '1.0', 1, window.location.href);
    } catch (error) {
      if (!cancelRef.current) setErrorMessage('Conversion failed. Please try again.');
      setSubmitValue('Convert');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    cancelRef.current = true;
    setIsProcessing(false);
    setSubmitValue('Convert');
    setProgress(0);
  };

  const handleDownload = () => {
    if (!result) return;
    const a = document.createElement('a');
    a.href = result.url;
    a.download = result.filename;
    a.click();
  };

  return (
    <div>
      <div className="service-card" onClick={toggleModal}>
        {isNew && <span className="badge badge-new">NEW</span>}
        <div className="service-card-icon">
          <svg viewBox="0 0 64 64" fill="none" style={{ width: 42, height: 42 }}>
            <defs>
              <linearGradient id="gsColorHalf" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#FF6B6B" />
                <stop offset="50%" stopColor="#4ECDC4" />
                <stop offset="100%" stopColor="#A78BFA" />
              </linearGradient>
              <clipPath id="gsLeftClip"><rect x="0" y="0" width="32" height="50" /></clipPath>
              <clipPath id="gsRightClip"><rect x="32" y="0" width="32" height="50" /></clipPath>
            </defs>
            {/* Color half (left) */}
            <g clipPath="url(#gsLeftClip)">
              <rect x="6" y="8" width="52" height="34" rx="3" fill="url(#gsColorHalf)" />
              <circle cx="18" cy="20" r="4" fill="#FFE66D" />
              <path d="M10 36 L20 26 L28 32 L40 20 L54 32 L54 40 L10 40 Z" fill="rgba(255,255,255,0.4)" />
            </g>
            {/* Grayscale half (right) */}
            <g clipPath="url(#gsRightClip)">
              <rect x="6" y="8" width="52" height="34" rx="3" fill="#777" />
              <circle cx="18" cy="20" r="4" fill="#999" />
              <path d="M10 36 L20 26 L28 32 L40 20 L54 32 L54 40 L10 40 Z" fill="rgba(255,255,255,0.2)" />
            </g>
            {/* Center divider */}
            <line x1="32" y1="10" x2="32" y2="40" stroke="#fff" strokeWidth="2" strokeDasharray="3 2" />
            {/* Dripping color drops */}
            <path d="M24 44 Q24 50 28 52 Q32 54 32 48" fill="#FF6B6B" />
            <ellipse cx="18" cy="54" rx="4" ry="5" fill="#4ECDC4" />
            <ellipse cx="44" cy="56" rx="3" ry="4" fill="#A78BFA" />
            {/* Mini saturation dial */}
            <circle cx="52" cy="52" r="8" fill="#f5f5f5" stroke="#ddd" strokeWidth="1" />
            <path d="M52 44 A8 8 0 1 1 44.5 56" stroke="#888" strokeWidth="2" fill="none" />
            <circle cx="52" cy="52" r="3" fill="#FF9900" />
          </svg>
        </div>
        <h3 className="service-card-title">Convert to Grayscale</h3>
        <p className="service-card-desc">Convert PDF or images to grayscale/black & white</p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
          </svg>
        </div>
      </div>

      <Modal
        onDismiss={toggleModal}
        visible={visible}
        size="medium"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={isProcessing ? handleCancel : toggleModal}>
                Cancel
              </Button>
              {!result ? (
                <Button variant="primary" onClick={handleConvert} disabled={isProcessing || !file}>
                  {submitValue}
                </Button>
              ) : (
                <Button variant="primary" onClick={handleDownload}>
                  Download
                </Button>
              )}
            </SpaceBetween>
          </Box>
        }
        header="Convert to Grayscale"
      >
        <div>
          <div className="bordered-box">
            <h6 className="container-header">📁 Upload File</h6>
            <FormField label="Select a PDF or image file (JPG, PNG)" errorText={errorMessage}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,image/jpeg,image/png,application/pdf"
                onChange={handleFileChange}
                disabled={isProcessing}
              />
            </FormField>
            {preview && (
              <div style={{ marginTop: '12px', textAlign: 'center' }}>
                <img
                  src={preview}
                  alt="Preview"
                  style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '8px', border: '1px solid #ddd' }}
                />
              </div>
            )}
            {file && isPdf(file) && (
              <div style={{ marginTop: '12px', fontSize: '13px', color: '#666' }}>
                📄 {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </div>
            )}
          </div>

          {isProcessing && isPdf(file) && (
            <div className="bordered-box">
              <FormField label="Conversion Progress">
                <ProgressBar value={progress} label={`${progress}%`} description="Processing pages..." />
              </FormField>
            </div>
          )}

          {result && (
            <div className="bordered-box">
              <h6 className="container-header">✅ Conversion Complete!</h6>
              <div
                style={{
                  backgroundColor: '#e6f7e6',
                  border: '1px solid #52c41a',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                }}
              >
                <span style={{ color: '#135200' }}>🎨 Grayscale conversion successful!</span>
              </div>
              {isImage(file) && (
                <div style={{ textAlign: 'center' }}>
                  <img
                    src={result.url}
                    alt="Grayscale result"
                    style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '8px', border: '1px solid #ddd' }}
                  />
                </div>
              )}
              <div style={{ textAlign: 'center', marginTop: '12px' }}>
                <Button variant="link" onClick={resetState}>
                  Convert Another File
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default ConvertToGrayscale;
